/**
 * Adlib panels and actions from the production's studio config snapshot
 * (D22): the panels' buttons and the free adlib's piece-type choices; and the
 * rundown's own adlib rows (PLAN §5.1, made in the editor), played by id
 * (`adlibFromRow`). The param form they open is `paramForm.ts`.
 *
 * The snapshot is parsed with the engine's schema (`parseProductionConfig`) once,
 * in the view; everything here reads the parsed config.
 */
import { readFields } from '@acp/domain/fields.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import type { Row } from '@acp/domain/row.js';
import { childrenOf, type RowTree } from '@acp/domain/tree.js';
import { checkParams } from '@acp/engine/params.js';
import type { ProductionConfig } from '@acp/engine/productionConfig.js';
import type { RundownSource } from './viewModel.js';

export type AdlibItemView =
  | {
      readonly kind: 'piece';
      readonly label: string;
      readonly pieceType: string;
      readonly pieceTypeLabel: string;
      readonly params: Readonly<Record<string, JsonValue>>;
      readonly mode: 'now' | 'queue';
      readonly hotkey: string | null;
      /** The piece type has params to fill in (the item's own are the form's starting values). */
      readonly hasParams: boolean;
      /** The config names a piece type the snapshot lacks: shown, and disabled. */
      readonly missing: boolean;
    }
  | { readonly kind: 'action'; readonly label: string; readonly actionId: string; readonly hotkey: string | null; readonly missing: boolean };

export type AdlibPanelView = { readonly id: string; readonly label: string; readonly items: readonly AdlibItemView[] };

export type ActionView = { readonly id: string; readonly label: string; readonly steps: number };

export function adlibPanelsOf(config: ProductionConfig): AdlibPanelView[] {
  return config.adlibPanels.map((panel) => ({
    id: panel.id,
    label: panel.label === '' ? panel.id : panel.label,
    items: panel.items.map((item): AdlibItemView => {
      if ('action' in item) return { kind: 'action', label: item.label, actionId: item.action, hotkey: item.hotkey, missing: !Object.hasOwn(config.actions, item.action) };
      const type = config.pieceTypes[item.pieceType];
      return {
        kind: 'piece',
        label: item.label,
        pieceType: item.pieceType,
        pieceTypeLabel: type === undefined || type.label === '' ? item.pieceType : type.label,
        params: item.params,
        mode: item.mode,
        hotkey: item.hotkey,
        hasParams: type !== undefined && Object.keys(type.params).length > 0,
        missing: type === undefined,
      };
    }),
  }));
}

export function actionsOf(config: ProductionConfig): ActionView[] {
  return Object.entries(config.actions).map(([id, a]) => ({ id, label: a.label === '' ? id : a.label, steps: a.steps.length }));
}

// ─── the rundown's own adlibs (adlib rows, PLAN §5.1) ───

/** One adlib row, as the operator screen offers it: played by its id (`adlibFromRow`), with the row's own type and params. */
export type RundownAdlibView = {
  readonly adlibId: string;
  readonly label: string;
  readonly pieceTypeLabel: string;
  /** How long it runs once played (the row's `expectedDuration`, else the type's default); null: until something ends it. */
  readonly durationMs: number | null;
  /** Why the production would refuse it — a type the snapshot lacks, params off the spec (`checkParams`) — or null. Shown, and disabled. */
  readonly problem: string | null;
};

/** The on-air part's adlibs, the next part's, and the rundowns' global ones (Sofie's shelf: the current part and the rundown baseline). */
export type RundownAdlibSection = { readonly id: string; readonly label: string; readonly items: readonly RundownAdlibView[] };

/**
 * Why the production would refuse an adlib row (as `adlibFromRow` checks it): a piece type the snapshot lacks,
 * params off its spec, a source layer the row wrote itself that the snapshot lacks — or null. Nothing to check
 * against without a snapshot.
 */
function adlibProblem(config: ProductionConfig | null, pieceType: string, params: Readonly<Record<string, JsonValue>>, ownSourceLayer: string | null): string | null {
  if (config === null) return null;
  const type = Object.hasOwn(config.pieceTypes, pieceType) ? config.pieceTypes[pieceType] : undefined;
  if (type === undefined) return pieceType === '' ? 'no piece type' : `no piece type "${pieceType}" in this production’s config snapshot`;
  const checked = checkParams(type, params);
  if (!checked.ok) return checked.error;
  if (ownSourceLayer !== null && !Object.hasOwn(config.sourceLayers, ownSourceLayer)) return `unknown source layer "${ownSourceLayer}"`;
  return null;
}

function adlibView(row: Row, config: ProductionConfig | null): RundownAdlibView | null {
  const read = readFields('adlib', row.f);
  if (!read.ok || read.value.kind !== 'adlib') return null;
  const { name, pieceType, params, expectedDuration, sourceLayer } = read.value.fields;
  // Only a layer the row wrote itself overrides the type's (the engine's `pickOverrides`).
  const ownSourceLayer = Object.hasOwn(row.f, 'sourceLayer') ? sourceLayer : null;
  const type = config !== null && Object.hasOwn(config.pieceTypes, pieceType) ? config.pieceTypes[pieceType] : undefined;
  const typeLabel = type === undefined || type.label === '' ? pieceType : type.label;
  const label = [name, typeLabel].find((l) => l !== '') ?? '(adlib)';
  return { adlibId: row.rowId, label, pieceTypeLabel: typeLabel, durationMs: expectedDuration ?? type?.defaultDuration ?? null, problem: adlibProblem(config, pieceType, params, ownSourceLayer) };
}

function adlibsUnder(tree: RowTree, parentId: string, config: ProductionConfig | null): RundownAdlibView[] {
  return childrenOf(tree, parentId).flatMap((row) => (row.kind === 'adlib' ? (adlibView(row, config) ?? []) : []));
}

function partSection(rundowns: readonly RundownSource[], partId: string, prefix: string, config: ProductionConfig | null): RundownAdlibSection | null {
  for (const r of rundowns) {
    const part = r.tree.rows.get(partId);
    if (part === undefined || part.kind !== 'part') continue;
    const items = adlibsUnder(r.tree, partId, config);
    const title = typeof part.f['title'] === 'string' && part.f['title'] !== '' ? part.f['title'] : '(untitled part)';
    return items.length === 0 ? null : { id: `${prefix}:${partId}`, label: `${prefix === 'onair' ? 'On air' : 'Next'} · ${title}`, items };
  }
  return null;
}

/**
 * The adlib rows the operator can play now (the engine plays any adlib row of
 * the playlist; the screen offers the ones that matter on air): the on-air
 * part's, the next part's, and each rundown's global adlibs — in running
 * order, each checked against the config snapshot as the production will.
 */
export function rundownAdlibsOf(rundowns: readonly RundownSource[], onAirPartId: string | null, nextPartId: string | null, config: ProductionConfig | null): RundownAdlibSection[] {
  const sections: RundownAdlibSection[] = [];
  const onAir = onAirPartId === null ? null : partSection(rundowns, onAirPartId, 'onair', config);
  if (onAir !== null) sections.push(onAir);
  const next = nextPartId === null || nextPartId === onAirPartId ? null : partSection(rundowns, nextPartId, 'next', config);
  if (next !== null) sections.push(next);
  for (const r of rundowns) {
    const items = adlibsUnder(r.tree, r.rundownId, config);
    if (items.length > 0) sections.push({ id: `global:${r.rundownId}`, label: rundowns.length > 1 ? `Rundown · ${r.name}` : 'Rundown', items });
  }
  return sections;
}

/** Every piece type, for the free adlib form (any type, any params). */
export function pieceTypeChoices(config: ProductionConfig): { readonly id: string; readonly label: string }[] {
  return Object.entries(config.pieceTypes).map(([id, t]) => ({ id, label: t.label === '' ? id : t.label }));
}
